import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/Global.css";
import Header from "../components/layout/Header";
import Spinner from "../components/Spinner";
import "../assets/css/Home.css";
import AddStudyModal from "../components/modals/AddStudyModal";
//import { getUserAndGeneratePDF } from "../report-logic/pdf-report";
import {
  fetchStudies,
  createStudy,
  updateStudy as apiUpdateStudy,
  activateStudy as apiActivateStudy,
} from "../services/apiServices";
import Footer from "../components/layout/Footer";
import { format } from "date-fns";
import { isAdmin, userRole } from "../services/config";

const Home = () => {
  const navigate = useNavigate();
  const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState({ add: false, edit: false });
  const [editingStudy, setEditingStudy] = useState(null);
  //const [activatingId, setActivatingId] = useState(null);

  useEffect(() => {
    const yearSpan = document.getElementById("year");
    if (yearSpan) yearSpan.textContent = new Date().getFullYear();
  }, []);

  // Load studies
  const loadStudies = async () => {
    setLoading(true);
    try {
      const data = await fetchStudies();
      setStudies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Refresh studies after any action
  const refreshStudies = loadStudies;

  // Add study handler
  const handleAddStudy = async (studyData) => {
    await createStudy(studyData);
    refreshStudies();
  };

  // Update study handler
  const handleUpdateStudy = async (studyData) => {
    if (!editingStudy) return;
    await apiUpdateStudy({ id: editingStudy.id, ...studyData });
    refreshStudies();
  };

  // Activate study handler
  // const handleMakeActive = async (study) => {
  //   //setActivatingId(study.id);
  //   await apiActivateStudy(study.id);
  //   refreshStudies();
  //   //setActivatingId(null);
  // };

  useEffect(() => {
    loadStudies();
  }, []);

  const normalizedStudies = useMemo(
    () =>
      studies.map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        startDate: s.startDate ?? s.start_date ?? null,
        closeDate: s.closeDate ?? s.close_date ?? null,
        createdAt: s.createdAt ?? s.created_at ?? null,
      })),
    [studies]
  );

  // Combine all studies and sort by status (active first) then by name
  const sortedStudies = useMemo(() => {
    return [...normalizedStudies].sort((a, b) => {
      // Active studies first
      if (!a.closeDate && b.closeDate) return -1;
      if (a.closeDate && !b.closeDate) return 1;
      // Then sort by name
      return a.name.localeCompare(b.name);
    });
  }, [normalizedStudies]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (e) {
      return "Invalid date";
    }
  };

  const getStatusBadge = (study) => {
    const isActive = !study.closeDate;
    return (
      <span
        className="study-badge"
        style={{
          background: isActive ? "#e8f7f0" : "#f3f4f6",
          color: isActive ? "#16a34a" : "#6b7280",
          border: `1px solid ${isActive ? "#16a34a" : "#d1d5db"}`,
        }}
      >
        {isActive ? "Active" : "Archived"}
      </span>
    );
  };

  return (
    <div>
      {loading ? (
        <div className="spinner-center" style={{ minHeight: "800px" }}>
          <Spinner label="Loading studies" />
        </div>
      ) : (
        <>
          <div className="container">
            <Header
              actions={
                <>
                  {loggedInUser?.source !== "user" && (
                    <button
                      className="button secondary"
                      onClick={() => navigate("/clinic")}
                    >
                      All Clinics
                    </button>
                  )}
                  <button
                    className="button primary"
                    onClick={() => setShowModal({ ...showModal, add: true })}
                  >
                    Add Study
                  </button></>
              }
            />

            <div className="table-container">
              {/* <button type="button" onClick={
                  ()=>getUserAndGeneratePDF('D57B9CE6-EB88-474D-AB4A-2F6BDF84CA7A')
                  }>Generate PDF Report</button> */}
              <table className="responsive-table">
                <thead>
                  <tr>
                    <th>Study Name</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Start Date</th>
                    <th>Close Date</th>
                    {isAdmin() && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedStudies.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="empty-state">
                        No studies found.
                      </td>
                    </tr>
                  ) : (
                    sortedStudies.map((study) => (
                     study.closeDate && userRole() !== "admin" ? null : (
                      <tr key={study.id} onClick={() => navigate(`/study/${study.id}`)}>
                        <td data-label="Study Name">{study.name}</td>
                        <td data-label="Description">{study.description || "-"}</td>
                        <td data-label="Status">{getStatusBadge(study)}</td>
                        <td data-label="Start Date">{formatDate(study.startDate)}</td>
                        <td data-label="Close Date">{study.closeDate ? formatDate(study.closeDate) : "-"}</td>
                        {isAdmin() && (
                          <td data-label="Actions" className="actions">
                            <button
                              className="button secondary small"
                              onClick={(e) => {
                                e.stopPropagation();
                              setEditingStudy(study);
                              setShowModal({ ...showModal, edit: true });
                            }}
                          >
                            Update
                          </button>
                        </td>)}
                      </tr>)
                    ))
                  )}
                </tbody>
              </table>

            </div>
          </div>
        </>
      )}

      <Footer />

      <AddStudyModal
        open={showModal.add || showModal.edit}
        onClose={() => {
          setShowModal({ add: false, edit: false });
          setEditingStudy(null);
        }}
        onSubmit={showModal.edit ? handleUpdateStudy : handleAddStudy}
        initial={showModal.edit ? editingStudy : null}
        title={showModal.edit ? "Update Study" : "Add Study"}
        submitLabel={showModal.edit ? "Update" : "Create"}
      />
    </div>
  );
};

export default Home;
